import { useState } from 'react'
import { useAdminApi } from '../api'
import { t, DictKey, categoriaLabel } from '../i18n'
import { useLang } from '../LangContext'
import { Icon } from '../../styles/Icon'

export interface EquipoEditable {
  id: string
  nombre: string
  categoria: string
  color?: string | null
  icono?: string | null
  minutos_por_periodo: number
  num_periodos: number
  dias_entrenamiento: number[]
}

const DIAS = [1, 2, 3, 4, 5, 6, 7]
const DIA_KEYS: Record<number, DictKey> = {
  1: 'lunes',
  2: 'martes',
  3: 'miercoles',
  4: 'jueves',
  5: 'viernes',
  6: 'sabado',
  7: 'domingo',
}

// Petición del club, 2026-10-02: "poder editar los equipos en cualquier momento. El nombre del
// equipo, los días, los tiempos..." — antes de esto, "Editar equipo" en el backoffice solo abría la
// gestión de la plantilla; los atributos del propio equipo no se podían tocar tras crearlo.
export function EditarEquipoForm({ equipo, onSaved }: { equipo: EquipoEditable; onSaved: () => void }) {
  const api = useAdminApi()
  const lang = useLang()
  const [form, setForm] = useState({
    nombre: equipo.nombre,
    categoria: equipo.categoria,
    minutos_por_periodo: equipo.minutos_por_periodo,
    num_periodos: equipo.num_periodos,
    dias_entrenamiento: equipo.dias_entrenamiento ?? [],
  })
  const [iconoFile, setIconoFile] = useState<File | null>(null)

  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  function toggleDia(dia: number) {
    setForm((f) => ({
      ...f,
      dias_entrenamiento: f.dias_entrenamiento.includes(dia)
        ? f.dias_entrenamiento.filter((d) => d !== dia)
        : [...f.dias_entrenamiento, dia],
    }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const icono = iconoFile ? await readFileAsDataUrl(iconoFile) : undefined
    try {
      await api.patch(`/admin/equipos/${equipo.id}`, {
        ...form,
        // Issue #42: UKE Eskola no tiene minutaje; se envía 0 en vez de mostrar los campos.
        ...(form.categoria === 'eskola' ? { minutos_por_periodo: 0, num_periodos: 0 } : {}),
        ...(icono ? { icono } : {}),
      })
      setIconoFile(null)
      onSaved()
    } catch (error) {
      window.alert(error instanceof Error ? error.message : t('error', lang))
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <h4>{t('datosEquipo', lang)}</h4>
      <label>
        {t('nombre', lang)}
        <input
          required
          value={form.nombre}
          onChange={(e) => setForm({ ...form, nombre: e.target.value })}
        />
      </label>
      <label>
        {t('categoria', lang)}
        <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
          <option value="eskola">{categoriaLabel('eskola', lang)}</option>
          <option value="f7">{categoriaLabel('f7', lang)}</option>
          <option value="f11">{categoriaLabel('f11', lang)}</option>
        </select>
      </label>
      <label>
        {t('icono', lang)}
        <input type="file" accept="image/*" onChange={(e) => setIconoFile(e.target.files?.[0] ?? null)} />
      </label>
      {form.categoria !== 'eskola' && (
        <>
          <label>
            {t('minutosPorPeriodo', lang)}
            <input
              type="number"
              value={form.minutos_por_periodo}
              onChange={(e) => setForm({ ...form, minutos_por_periodo: Number(e.target.value) })}
            />
          </label>
          <label>
            {t('numPeriodos', lang)}
            <input
              type="number"
              value={form.num_periodos}
              onChange={(e) => setForm({ ...form, num_periodos: Number(e.target.value) })}
            />
          </label>
        </>
      )}
      <fieldset>
        <legend>{t('diasEntrenamiento', lang)}</legend>
        {DIAS.map((dia) => (
          <label key={dia}>
            <input
              type="checkbox"
              checked={form.dias_entrenamiento.includes(dia)}
              onChange={() => toggleDia(dia)}
            />
            {t(DIA_KEYS[dia], lang)}
          </label>
        ))}
      </fieldset>
      <button type="submit"><Icon name="guardar" />{t('guardar', lang)}</button>
    </form>
  )
}
